/**
 * Sync Engine - Processes offline queue entries when connection is restored
 *
 * Features:
 * - Exponential backoff: 1s, 2s, 4s, 8s, max 30s
 * - Last-Write-Wins conflict resolution based on updated_at
 * - Processing order: inspections → responses → defects (dependency chain)
 * - Image upload: loads Base64 from offline-images, uploads to storage, replaces URL in payload
 * - Progress callback for UI updates
 *
 * @see .lovable/memory/architecture/offline-first-infrastructure.md
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import {
  dequeueAll,
  removeFromQueue,
  updateQueueEntry,
  getImage,
  removeImage,
  type SyncQueueEntry,
} from "./offlineStore";
import { getSyncEntryHandler, LWW_CONFLICT_PREFIX, type SyncEntryTable } from "./syncEntryHandlers";

const MAX_BACKOFF_MS = 30_000;
const BASE_DELAY_MS = 1_000;
const MAX_RETRIES = 10;

/** Priority order for entity types (lower = processed first) */
const ENTITY_PRIORITY: Record<SyncQueueEntry["entityType"], number> = {
  inspection: 0,
  inspection_response: 1,
  defect: 2,
  defect_comment: 3,
};

export interface SyncProgress {
  current: number;
  total: number;
  currentEntry?: SyncQueueEntry;
}

export interface SyncResult {
  synced: number;
  failed: number;
  errors: Array<{ entry: SyncQueueEntry; error: string }>;
}

export interface SyncCallbacks {
  onProgress?: (progress: SyncProgress) => void;
  onComplete?: (result: SyncResult) => void;
  onError?: (error: string) => void;
}

function getBackoffDelay(retryCount: number): number {
  const delay = BASE_DELAY_MS * Math.pow(2, retryCount);
  return Math.min(delay, MAX_BACKOFF_MS);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Sort entries by entity priority then by timestamp */
function sortEntries(entries: SyncQueueEntry[]): SyncQueueEntry[] {
  return [...entries].sort((a, b) => {
    const priorityDiff = ENTITY_PRIORITY[a.entityType] - ENTITY_PRIORITY[b.entityType];
    if (priorityDiff !== 0) return priorityDiff;
    return a.timestamp.localeCompare(b.timestamp);
  });
}

/** Upload images referenced in a sync entry and return updated payload */
// fallow-ignore-next-line complexity
async function uploadImages(entry: SyncQueueEntry): Promise<Record<string, unknown>> {
  const payload = { ...entry.payload };

  // Remove internal-only fields that should never be sent to Supabase
  const internalImageRefs = (payload._imageRefs as string[] | undefined) || [];
  const existingImages = (payload.existing_images as string[] | undefined) || [];
  delete payload._imageRefs;
  delete payload.existing_images;

  // Determine which image refs to process (prefer entry.imageRefs, fallback to _imageRefs from payload)
  const imageRefs = entry.imageRefs?.length ? entry.imageRefs : internalImageRefs;

  if (imageRefs.length === 0) {
    // Even with no new images, merge existing_images into payload.images
    if (existingImages.length > 0) {
      const current = (payload.images as string[] | null) || [];
      payload.images = [...existingImages, ...current];
    }
    return payload;
  }

  // Resolve tenant for storage path - no silent fallback (No-Implicit-Fallbacks policy)
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    throw new Error("Cannot upload images: user not authenticated");
  }
  const { data: profile } = await supabase
    .from("profiles")
    .select("active_tenant_id")
    .eq("user_id", user.id)
    .single();
  if (!profile?.active_tenant_id) {
    throw new Error("Cannot upload images: no active tenant");
  }
  const tenantPrefix = profile.active_tenant_id;

  const uploadedPaths: string[] = [];

  for (const imageId of imageRefs) {
    const base64 = await getImage(imageId);
    if (!base64) {
      logger.error(`Offline image ${imageId} not found in IndexedDB - data may have been purged`);
      throw new Error(
        `Offline image lost: ${imageId}. Browser storage was cleared before sync could complete.`,
      );
    }

    // Convert base64 to blob
    const byteString = atob(base64.split(",")[1] || base64);
    const mimeType = base64.match(/data:([^;]+);/)?.[1] || "image/jpeg";
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeType });

    const ext = mimeType.split("/")[1] || "jpg";
    const filePath = `${tenantPrefix}/${entry.entityType}s/${Date.now()}-${imageId.slice(-8)}.${ext}`;
    const { data: uploadData, error } = await supabase.storage
      .from("checklist-images")
      .upload(filePath, blob, { upsert: true });

    if (error) {
      logger.error(`Image upload failed for ${imageId}: ${error.message}`);
      throw new Error(`Image upload failed: ${error.message}. Will retry on next sync.`);
    }
    uploadedPaths.push(uploadData!.path);
    await removeImage(imageId);
  }

  // Merge: existing images + newly uploaded images
  const allImages = [...existingImages, ...uploadedPaths];
  if (allImages.length > 0) {
    payload.images = allImages;
  }

  return payload;
}

/** Map entity type to Supabase table name */
// fallow-ignore-next-line complexity
function getTableName(entityType: SyncQueueEntry["entityType"]): string {
  switch (entityType) {
    case "inspection":
      return "inspections";
    case "inspection_response":
      return "inspection_responses";
    case "defect":
      return "defects";
    case "defect_comment":
      return "defect_comments";
  }
}

/** Process a single sync queue entry */
async function processEntry(entry: SyncQueueEntry): Promise<void> {
  const payload = await uploadImages(entry);
  const tableName = getTableName(entry.entityType);

  // Use dynamic table access - types are not known at compile time for generic sync.
  // The table name is resolved at runtime, so we bridge the per-table query builder
  // to the minimal structural SyncEntryTable facade via `unknown` (never `any`).
  const table = supabase.from(tableName as "inspections") as unknown as SyncEntryTable;
  const handler = getSyncEntryHandler(entry.operation);
  await handler({ entry, payload, table });
}

/**
 * Process all entries in the sync queue
 * Call this when the device comes back online
 */
// fallow-ignore-next-line complexity
export async function processSyncQueue(callbacks?: SyncCallbacks): Promise<SyncResult> {
  // Skip dead-lettered entries: they exhausted MAX_RETRIES and are kept only for recovery,
  // so they must not be re-processed on every run. (TES-789)
  const all = await dequeueAll();
  const entries = all.filter((e) => !e.failed);
  if (entries.length === 0) {
    logger.info("Sync queue empty — nothing to process", {
      total: all.length,
      deadLettered: all.length - entries.length,
    });
    const result: SyncResult = { synced: 0, failed: 0, errors: [] };
    callbacks?.onComplete?.(result);
    return result;
  }

  logger.info("Processing sync queue", {
    pending: entries.length,
    deadLettered: all.length - entries.length,
  });

  const sorted = sortEntries(entries);
  const result: SyncResult = { synced: 0, failed: 0, errors: [] };

  for (let i = 0; i < sorted.length; i++) {
    const entry = sorted[i];

    callbacks?.onProgress?.({
      current: i + 1,
      total: sorted.length,
      currentEntry: entry,
    });

    logger.debug("Processing sync entry", {
      entityType: entry.entityType,
      id: entry.id,
      operation: entry.operation,
      attempt: entry.retryCount + 1,
      position: i + 1,
      total: sorted.length,
    });

    try {
      if (entry.retryCount > 0) {
        const backoffMs = getBackoffDelay(entry.retryCount);
        logger.debug("Backing off before retry", {
          entityType: entry.entityType,
          id: entry.id,
          attempt: entry.retryCount + 1,
          backoffMs,
        });
        await sleep(backoffMs);
      }

      await processEntry(entry);
      await removeFromQueue(entry.id!);
      result.synced++;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);

      const isLwwConflict = errorMessage.startsWith(LWW_CONFLICT_PREFIX);

      if (isLwwConflict || entry.retryCount >= MAX_RETRIES) {
        // Dead-letter instead of deleting: keep the entry — and the user's queued data — so it
        // is recoverable/surfaceable instead of silently destroyed. An LWW conflict (TES-788)
        // will never resolve on retry, so dead-letter it immediately; MAX_RETRIES (TES-789)
        // likewise. The `failed` flag makes processSyncQueue skip it on the next run.
        await updateQueueEntry({ ...entry, failed: true, failedReason: errorMessage });
        result.failed++;
        logger.warn("Sync entry dead-lettered (kept for recovery)", {
          entityType: entry.entityType,
          id: entry.id,
          attempt: entry.retryCount + 1,
          reason: isLwwConflict ? "lww_conflict" : "max_retries_exceeded",
        });
        result.errors.push({
          entry,
          error: isLwwConflict
            ? `Conflict — server has a newer version (dead-lettered, kept for recovery): ${errorMessage}`
            : `Max retries exceeded (dead-lettered, kept for recovery): ${errorMessage}`,
        });
        callbacks?.onError?.(
          `${isLwwConflict ? "Sync conflict" : "Max retries exceeded"} for ${entry.entityType} (kept for recovery): ${errorMessage}`,
        );
      } else {
        // Increment retry count and keep in queue
        await updateQueueEntry({
          ...entry,
          retryCount: entry.retryCount + 1,
        });
        result.failed++;
        logger.warn("Sync entry failed — will retry", {
          entityType: entry.entityType,
          id: entry.id,
          nextAttempt: entry.retryCount + 2,
        });
        result.errors.push({ entry, error: errorMessage });
      }
    }
  }

  logger.info("Sync queue processed", {
    synced: result.synced,
    failed: result.failed,
    total: sorted.length,
  });

  callbacks?.onComplete?.(result);
  return result;
}
